import React, { useEffect, useState } from 'react';
import { useImmer } from 'use-immer';
import { useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { moduleClientFactory } from '../../../../api-service/module-client-factory';
import { ICompanyLookup } from '../../../../components/lookups/company-lookup/interfaces';
import { useMetaTitle } from '../../../../hooks';
import { usePostToTab } from '../../../search/hooks/usePostToTab';
import { ClassroomComponent, ClassroomErrors, ClassroomState } from './classroom-component';
import { useAttendanceRights } from '../../use-attendance-rights';

const client = moduleClientFactory.createAttendanceClient();

export const REFRESH_CLASSROOM_TABLE = 'REFRESH_CLASSROOM_TABLE';

export function Classroom() {
  const { classroomId } = useParams<{ classroomId?: string }>();
  const [classroom, setClassroom] = useImmer<ClassroomState>({ isActive: true });
  const [errors, setErrors] = useImmer<ClassroomErrors>({});
  const [isSaving, setIsSaving] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const postUpdate = usePostToTab(REFRESH_CLASSROOM_TABLE);
  const { canEditClassrooms } = useAttendanceRights();

  useMetaTitle(classroomId ? 'Redigér lokale' : 'Opret lokale');

  useEffect(() => {
    async function loadClassroom() {
      if (!classroomId) {
        return;
      }
      try {
        const result = await client.getClassRoomById({ classroomId });
        if (result.classroom) {
          const { name, companyId, isActive } = result.classroom;
          setClassroom({ name, companyId, isActive });
        }
      } catch (error) {
        enqueueSnackbar('Der skete en fejl ved hentning af lokale', { variant: 'error' });
      }
    }
    loadClassroom();
  }, [classroomId]);

  function handleValidate() {
    const validationErrors: ClassroomErrors = {};
    if (!classroom.name) {
      validationErrors.name = 'Navn skal angives';
    }
    if (!classroom.companyId) {
      validationErrors.companyId = 'Virksomhed skal angives';
    }
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  }

  async function handleSaveClick() {
    const { name, companyId, isActive } = classroom;
    if (!name || !companyId) {
      return;
    }
    try {
      setIsSaving(true);
      if (classroomId) {
        await client.updateClassRoom({ classroom: { classroomId, companyId, name, isActive, isDeleted: false } });
      } else {
        await client.createClassRoom({ classroom: { companyId, name, isActive, isDeleted: false } });
      }
      postUpdate(window.opener);
      window.close();
    } catch (error) {
      enqueueSnackbar('Der skete en fejl ved gemning af lokale', { variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  }

  function handleNameChange(name: string) {
    setClassroom(draft => {
      draft.name = name;
    });
    setErrors(draft => {
      draft.name = undefined;
    });
  }

  function handleCompanyChange(company?: ICompanyLookup) {
    setClassroom(draft => {
      draft.companyId = company?.id;
    });
    setErrors(draft => {
      draft.companyId = undefined;
    });
  }

  function handleIsActiveChange(isActive: boolean) {
    setClassroom(draft => {
      draft.isActive = isActive;
    });
  }

  return (
    <ClassroomComponent
      isEditMode={!!classroomId}
      isReadOnly={!canEditClassrooms}
      isSaving={isSaving}
      classroom={classroom}
      errors={errors}
      onSaveClick={handleSaveClick}
      onValidate={handleValidate}
      onNameChange={handleNameChange}
      onCompanyChange={handleCompanyChange}
      onIsActiveChange={handleIsActiveChange}
    />
  );
}
